import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  isValidElement,
} from 'react';
import { type ListChildComponentProps, VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 8;
const DEFAULT_CELL_SIZE = 36;

const Row = ({
  data,
  index,
  setSize,
  style,
}: ListChildComponentProps & {
  // eslint-disable-next-line no-unused-vars
  setSize: (index: number, height: number) => void;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!rowRef.current) {
      return;
    }

    setSize(index, rowRef.current.getBoundingClientRect().height);
  }, [setSize, index]);

  return (
    <div style={{ ...style, top: (style.top as number) + LISTBOX_PADDING }}>
      <div ref={rowRef}>{data[index]}</div>
    </div>
  );
};

const OuterElementContext = createContext<React.HTMLAttributes<HTMLDivElement>>({});

const OuterElementType = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    const outerProps = useContext(OuterElementContext);

    return (
      <div
        ref={ref}
        {...props}
        {...outerProps}
        onScroll={(event) => {
          props.onScroll?.(event);
          outerProps.onScroll?.(event);
        }}
      />
    );
  }
);

OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null);

  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);

  return ref;
}

interface ListBoxProps {
  children?: React.ReactNode;
  size?: number;
  spacing?: number;
  listClassName?: string;
  dynamicHeight?: boolean;
  dynamicHeightRef?: React.RefObject<HTMLDivElement>;
}

export const Listbox = forwardRef<HTMLDivElement, ListBoxProps>(
  (
    {
      children,
      size = DEFAULT_CELL_SIZE,
      spacing = 0,
      listClassName,
      dynamicHeight = false,
      dynamicHeightRef,
      ...other
    }: ListBoxProps,
    ref
  ) => {
    const [, setMeasuredSizeVersion] = useState(0);
    const sizeMap = useRef<{ [k: number]: number }>({});

    const itemData = useMemo(() => Children.toArray(children), [children]);
    const itemCount = itemData.length;

    const gridRef = useResetCache(itemCount);

    const activeDescendant = (other as Record<string, unknown>)?.[
      'aria-activedescendant'
    ] as string | undefined;

    const highlightedIndex = useMemo(() => {
      if (activeDescendant) {
        const activeIndex = itemData.findIndex(
          (item) =>
            isValidElement<React.HTMLAttributes<HTMLElement>>(item) &&
            item.props.id === activeDescendant
        );

        if (activeIndex !== -1) {
          return activeIndex;
        }
      }

      return itemData.findIndex(
        (item) =>
          isValidElement<React.HTMLAttributes<HTMLElement>>(item) &&
          (item.props['aria-selected'] === true || item.props['aria-selected'] === 'true')
      );
    }, [activeDescendant, itemData]);

    const getDynamicHeight = useCallback(
      (cell_size = DEFAULT_CELL_SIZE) => {
        if (dynamicHeight && dynamicHeightRef?.current) {
          return dynamicHeightRef.current.offsetHeight;
        }

        const visibleItemCount = Math.min(itemCount, 8);
        let height = 0;

        for (let itemIndex = 0; itemIndex < visibleItemCount; itemIndex += 1) {
          height += (sizeMap.current[itemIndex] || cell_size) + spacing;
        }

        return height;
      },
      [dynamicHeight, dynamicHeightRef, itemCount, spacing]
    );

    const setSize = useCallback(
      (index: number, size: number) => {
        if (sizeMap.current[index] === size) {
          return;
        }

        sizeMap.current = { ...sizeMap.current, [index]: size };
        gridRef.current?.resetAfterIndex(index);
        setMeasuredSizeVersion((version) => version + 1);
      },
      [gridRef]
    );

    useEffect(() => {
      if (highlightedIndex < 0) {
        return;
      }

      gridRef.current?.scrollToItem(highlightedIndex, 'smart');
    }, [highlightedIndex]);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getDynamicHeight(size) + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={(index) => (sizeMap.current[index] || DEFAULT_CELL_SIZE) + spacing}
            overscanCount={5}
            itemCount={itemCount}
            className={listClassName}
            style={{ padding: 0 }}
          >
            {({ data, index, style }) => (
              <Row data={data} index={index} setSize={setSize} style={style} />
            )}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);
